<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { Star, X } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { CallsService } from '@/types/generated'

  const props = defineProps<{
    callUuid: string
    initialRating?: number
    isOpen: boolean
  }>()

  const emit = defineEmits<{
    (event: 'ratingUpdated', rating: string): void
    (event: 'close'): void
  }>()

  const rating = ref(props.initialRating || 0)
  const comment = ref('')
  const isSubmitting = ref(false)

  const stars = computed(() => {
    const result = []
    for (let i = 1; i <= 5; i++) {
      const filled = rating.value >= i
      result.push({ value: i, filled })
    }
    return result
  })

  const setRating = (value: number) => {
    rating.value = rating.value === value ? value - 1 : value
  }

  const submitRating = async () => {
    isSubmitting.value = true
    const ratingString = rating.value === 0 ? '0.0' : rating.value.toFixed(1)
    try {
      emit('ratingUpdated', ratingString)
      emit('close')
      await CallsService.callsLawyerFeedbackCreate({
        requestBody: {
          rating: ratingString,
          feedback: comment.value,
          call_uuid: props.callUuid
        }
      })
    } catch (error) {
      console.error('Error submitting rating:', error)
    } finally {
      isSubmitting.value = false
    }
  }
</script>

<template>
  <Dialog
    :open="isOpen"
    @update:open="emit('close')"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="text-center text-xl font-semibold">
          Wie bewerten Sie den KI-Anrufbeantworter in diesem Anruf?
        </DialogTitle>
      </DialogHeader>

      <!-- Stars -->
      <div class="flex justify-center gap-2 py-4">
        <button
          v-for="star in stars"
          :key="star.value"
          class="transform transition-all duration-200 hover:scale-110 focus:outline-none"
          @click="setRating(star.value)"
        >
          <Star
            size="36"
            :class="star.filled ? 'fill-current text-purple-600' : 'text-gray-300'"
          />
        </button>
      </div>

      <!-- Comment -->
      <div class="space-y-2">
        <label class="text-sm font-medium text-gray-700">Kommentar (optional)</label>
        <TextAreaField
          v-model="comment"
          placeholder="Ihr Kommentar..."
          rows="3"
          name="comment"
          class="resize-none"
        />
      </div>

      <!-- Submit Button -->
      <Button
        class="w-full bg-purple-600 text-white hover:bg-purple-700"
        :disabled="isSubmitting || rating === 0"
        @click="submitRating"
      >
        {{ isSubmitting ? 'Speichere...' : 'Bewertung speichern' }}
      </Button>
    </DialogContent>
  </Dialog>
</template>
